import { matchPath } from "react-router-dom"
import { coerce, number, object, string, validate } from "superstruct"

import { CATALOG_PATH } from "./catalogPath"

const pageNumber = coerce(number(), string(), (value) => parseInt(value, 10))
const filterPage = object({
    filter: string(),
})
const anyPage = object({
    page: pageNumber,
})
const cityFilter = object({
    city: string(),
    filter: string(),
})
const cityFilterPage = object({
    city: string(),
    filter: string(),
    page: pageNumber,
})
const wrongPage = object({
    filter: pageNumber,
})
const anyFilterPage = object({
    page: pageNumber,
    filter: string(),
})

type SubRouteParams =
    | { page: number }
    | { filter: string }
    | { filter: string; page: number }
    | { city: string; filter: string }
    | { city: string; filter: string; page: number }
    | undefined

type SubRouteResult = {
    routes: {
        path: string
        withPageNumber: string
    }
    params: SubRouteParams
}

const createSubRoute = <T extends Record<string, string | number> | undefined>(
    path: string,
    withPageNumber: string,
    params: T
): SubRouteResult => {
    return {
        routes: {
            path,
            withPageNumber,
        },
        params: params as SubRouteParams,
    }
}

const resolveOnlineSubRoute = (pathname: string): SubRouteResult | null => {
    const matchCatalogOnline = matchPath(CATALOG_PATH.online, pathname) ?? undefined
    const matchAnyPageOnline = matchPath(CATALOG_PATH.anyPageOnline, pathname) ?? undefined
    if (!matchCatalogOnline && !matchAnyPageOnline) return null

    const [, params] = validate(matchAnyPageOnline?.params, anyPage, { coerce: true })
    return createSubRoute(
        CATALOG_PATH.online,
        CATALOG_PATH.anyPageOnline,
        params as { page: number } | undefined
    )
}

const resolveAnyPageCityFilterSubRoute = (pathname: string): SubRouteResult | null => {
    const matchAnyPageWithCityFilter =
        matchPath(CATALOG_PATH.anyPageCityFilter, pathname) ?? undefined
    if (!matchAnyPageWithCityFilter) return null

    const [error, params] = validate(matchAnyPageWithCityFilter?.params, cityFilterPage, {
        coerce: true,
    })
    if (error) return null

    return createSubRoute(
        CATALOG_PATH.cityFilter,
        CATALOG_PATH.anyPageCityFilter,
        params as { city: string; filter: string; page: number }
    )
}

const resolveAnyPageFilterSubRoute = (pathname: string): SubRouteResult | null => {
    const matchAnyPageWithFilter = matchPath(CATALOG_PATH.anyPageFilter, pathname) ?? undefined
    if (!matchAnyPageWithFilter) return null

    const [error, params] = validate(matchAnyPageWithFilter?.params, anyFilterPage, {
        coerce: true,
    })
    if (error) return null

    return createSubRoute(
        CATALOG_PATH.anyFilter,
        CATALOG_PATH.anyPageFilter,
        params as { filter: string; page: number }
    )
}

const resolveCityFilterSubRoute = (pathname: string): SubRouteResult | null => {
    const matchCityFilter = matchPath(CATALOG_PATH.cityFilter, pathname) ?? undefined
    if (!matchCityFilter) return null

    const [error, params] = validate(matchCityFilter?.params, cityFilter, { coerce: true })
    if (error || /^\d+$/.test((params as { filter: string }).filter)) return null

    return createSubRoute(
        CATALOG_PATH.cityFilter,
        CATALOG_PATH.anyPageCityFilter,
        params as { city: string; filter: string }
    )
}

const resolveAnyFilterSubRoute = (pathname: string): SubRouteResult | null => {
    const matchAnyFilter = matchPath(CATALOG_PATH.anyFilter, pathname) ?? undefined
    if (!matchAnyFilter) return null

    const [, wrongPageParams] = validate(matchAnyFilter?.params, wrongPage, {
        coerce: true,
    })
    if (wrongPageParams) {
        return createSubRoute(CATALOG_PATH.root, CATALOG_PATH.anyPage, {
            page: (wrongPageParams as { filter: number }).filter,
        })
    }

    const [, params] = validate(matchAnyFilter?.params, filterPage, { coerce: true })
    return createSubRoute(
        CATALOG_PATH.anyFilter,
        CATALOG_PATH.anyPageFilter,
        params as { filter: string }
    )
}

const resolveRootSubRoute = (pathname: string): SubRouteResult => {
    const match = matchPath(CATALOG_PATH.root, pathname) ?? undefined
    const [, params] = validate(match?.params, anyPage, { coerce: true })
    return createSubRoute(
        CATALOG_PATH.root,
        CATALOG_PATH.anyPage,
        params as { page: number } | undefined
    )
}

export const getSubRoutes = (pathname: string): SubRouteResult => {
    return (
        resolveOnlineSubRoute(pathname) ||
        resolveAnyPageCityFilterSubRoute(pathname) ||
        resolveAnyPageFilterSubRoute(pathname) ||
        resolveCityFilterSubRoute(pathname) ||
        resolveAnyFilterSubRoute(pathname) ||
        resolveRootSubRoute(pathname)
    )
}
